import { get, isObject, has } from 'lodash'
import { ComponentTypes } from '@wix/wix-data-client-common'
import { FieldType } from '@wix/wix-data-schema-types'
import { DYNAMIC_REPEATER_ROLE } from '@wix/wix-data-client-common-standalone'

import type { ConnectionConfigEventName } from '../../types'
import type {
  AdaptedComponent,
  ComponentAdapter,
  ComponentValueConfig,
} from './componentFactory'
import type {
  WixComponent,
  WixRepeaterComponent,
  WixDynamicRepeaterComponent,
} from './types'
import type { AdaptedRepeaterComponent } from './repeaterAdapter'
import { repeaterAdapter } from './repeaterAdapter'
import type { AdaptedDynamicRepeaterComponent } from './dynamicRepeaterAdapter'
import { dynamicRepeaterAdapter } from './dynamicRepeaterAdapter'
import { appContext } from '../../viewer-app-module/DataBindingAppContext'

const isInput = (component: WixComponent) =>
  'onChange' in component && ('value' in component || 'checked' in component)

const isLink = (fieldType?: FieldType) =>
  fieldType === FieldType.url || fieldType === FieldType.pageLink

const isRecord = (value: any) => isObject(value) && has(value, '_id')

const getPath = (component: WixComponent, propPath: string) => {
  const propNames = propPath.split('.')
  const path = propNames.slice(0, -1)
  const [prop] = propNames.slice(-1)
  const obj = path.length > 0 ? get(component, path) : component

  return { obj, prop }
}

export const findParentRepeater = (
  component: WixComponent,
): AdaptedRepeaterComponent | AdaptedDynamicRepeaterComponent | undefined => {
  // In Builder, parent is undefined for components.
  if (appContext.platform.settings.env.builder || !component || !component.parent) {
    return undefined
  }
  const { parent } = component

  if (parent.type === ComponentTypes.Repeater) {
    return repeaterAdapter(parent as WixRepeaterComponent)
  }

  if (parent.role === DYNAMIC_REPEATER_ROLE) {
    return dynamicRepeaterAdapter(parent as WixDynamicRepeaterComponent)
  }

  return findParentRepeater(parent)
}

export const baseComponentAdapter: ComponentAdapter = (component) => {
  const { connectionConfig, uniqueId, type, role, id: nickname } = component
  const isHiddenInitially = !!component.hidden

  const api: AdaptedComponent = {
    id: uniqueId,
    connectionConfig,
    nickname,
    type: type,
    role: role,
    isInput: isInput(component),
    parentRepeater: findParentRepeater(component),
    getValue({ propPath }: ComponentValueConfig = {}) {
      if (propPath) {
        const { obj, prop } = getPath(component, propPath)
        return obj[prop]
      }
      return undefined
    },
    setValue(
      value: any,
      { propPath, fieldType, binding }: ComponentValueConfig = {},
    ) {
      if (propPath) {
        const { obj, prop } = getPath(component, propPath)
        obj[prop] =
          fieldType === 'reference' && isRecord(value) ? value._id : value

        if (typeof binding !== 'undefined' && isLink(fieldType)) {
          if (binding.linkTarget) {
            obj['target'] = binding.linkTarget
          }
          if (
            typeof binding.linkRel !== 'undefined'
            // There can be an empty string.
          ) {
            obj['rel'] = binding.linkRel
          }
        }
      }
    },
    setOptions(options) {
      if ('options' in component) {
        component.options = options
      }
    },
    hide() {
      if (!component.hidden) {
        component.hide?.()
      }
    },
    show({ ignoreInitiallyHidden } = { ignoreInitiallyHidden: false }) {
      if (!component.hidden || (ignoreInitiallyHidden && isHiddenInitially)) {
        return
      }
      component.show?.()
    },
    clear({ fieldType, propPath, binding }: ComponentValueConfig = {}) {
      // TODO: used instead of getEmptyValueForFieldType, should be handled by own adapter
      // but due to uncertainty about deffered dataset, may be temporary calculated here
      const emptyValue = fieldType === FieldType.mediaGallery ? [] : undefined
      api.setValue(emptyValue, { fieldType, propPath, binding })
    },
    get enabled() {
      return 'enabled' in component ? component.enabled : true
    },
    enable() {
      if ('enable' in component && !api.enabled) {
        component.enable()
      }
    },
    disable() {
      if ('disable' in component && api.enabled) {
        component.disable()
      }
    },
    focus() {
      if ('focus' in component && typeof component.focus === 'function') {
        component.focus()
      }
    },
    isValid() {
      if ('validity' in component && typeof component.validity === 'object') {
        return component.validity.valid as boolean
      }
      return true
    },
    updateValidityIndication() {
      if ('updateValidityIndication' in component) {
        component.updateValidityIndication()
      }
    },
    resetValidityIndication() {
      if ('resetValidityIndication' in component) {
        component.resetValidityIndication()
      }
    },
    onChange(callback: (event: any) => void) {
      api.on('onChange', callback)
    },
    on(eventName: ConnectionConfigEventName, callback: (event: any) => void) {
      component[eventName]?.(callback)
    },
  }

  if ('placeholder' in component) {
    Object.defineProperty(api, 'placeholder', {
      get() {
        return component.placeholder
      },
    })
  }

  return api
}
